import React, { JSX } from 'react';

import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  Col,
  Cols,
  IndependentSelectPill,
  spacing,
  Text,
  TextButton,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';

interface IProps {
  labelWidth: number;
  labelHeight: number;
  cypressParentName: string;
  enableClear: boolean; // true will display the clear filters button
  filtersConfig: Record<string, IFilterRowObject>;
  filtersToOptionsMap: Record<string, ISelectOption[]>;
  handleClearFilters: () => void; // clear all filters function
  handleApplySelect: (value: Set<number | string>, key?: string | number) => void;
  selectedFilters: Record<string, Set<string | number>>; // selected filters to display in the filter pills
  loadState: LoadingState; // loading state for the filters
}

export const Filters = ({
  cypressParentName,
  labelWidth,
  labelHeight,
  enableClear,
  filtersConfig,
  filtersToOptionsMap,
  handleClearFilters,
  handleApplySelect,
  selectedFilters,
  loadState,
}: IProps): JSX.Element => {
  const { isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  return (
    <Cols
      gap={spacing.base}
      style={{ alignItems: 'center', minHeight: `${labelHeight}px` }}
      className={cypressParentName}
    >
      {/* LABEL */}
      <Col style={{ minWidth: `${labelWidth}px` }}>
        <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
          <Col displayFlex>
            <IconV2 name='filter' small />
          </Col>
          <Col>
            <Text small bold tKey='Filters' singleLineLabel />
          </Col>
        </Cols>
      </Col>
      {/* Filters */}
      <Col flex>
        <Cols
          wrap
          style={{
            alignItems: 'center',
            padding: '5px 8px 5px 0px',
            columnGap: '2px',
            rowGap: spacing.extraSmall,
          }}
        >
          {loadState !== LoadingStateLoading &&
            Object.values(filtersConfig).map((filter) => {
              if (filter.isSuperUserOnly && !isSuperUser) {
                return;
              }
              return (
                <Col key={filter.key}>
                  <IndependentSelectPill
                    disableSearch={filter.disableSearch || false}
                    disableSort={filter.disableSort || false}
                    displayID={filter.displayID || false}
                    displayLabelWithSelection={filter.displayLabelWithSelection || false}
                    displaySelectedOption={filter.displaySelectedOption || false}
                    enableActiveFilter={filter.enableActiveFilter || false}
                    enableLabelTooltip={filter.enableLabelTooltip || false}
                    extractStringToSort={filter.extractStringToSort}
                    handleApplySelect={handleApplySelect}
                    isCheckboxHidden={filter.isCheckboxHidden || false}
                    isDisabled={false}
                    isSelectionRequired={filter.isSelectionRequired || false}
                    key={filter.key}
                    labelTKey={filter.tKey}
                    onLoadDisplayActiveOnly={filter.onLoadDisplayActiveOnly || false}
                    options={filtersToOptionsMap[filter.key] || []}
                    selectKey={filter.key}
                    selectedLimit={filter.selectedLimit || 0}
                    selectedOptionIDs={selectedFilters[filter.key] || new Set()}
                    syncWithParent
                    presets={filter.presets}
                    hasChildrenIDs={filter.hasChildrenIDs || false}
                  />
                </Col>
              );
            })}
          {enableClear && (
            <Col displayFlex smallHorizontalPadding>
              <TextButton size='extraSmall' onClick={handleClearFilters}>
                {t('ClearAll')}
              </TextButton>
            </Col>
          )}
        </Cols>
      </Col>
    </Cols>
  );
};
